/**
 * Rolling Average utility functions for Bar Chart, Table & Crosstab visualizations.
 * Provides window size options, helper text, and display formatting.
 */

import type { TimePeriod } from "@/store/slices/crosstab-config-slice";
import type { TimeUnit } from "@/store/slices/line-chart-config-slice";
import { getTimePeriodNominalRepresentation, MONTH_NAMES } from "@/utils/time-period-display";

export interface RollingAverageConfig {
  enabled: boolean;
  windowSize: number; // 2, 3, or 4
}

interface WindowOption {
  value: number;
  label: string;
}

/**
 * Get the period type label for a given TimePeriod selection.
 */
export function getPeriodTypeLabel(timePeriod: TimePeriod): string {
  switch (timePeriod) {
    case "latest_month":
    case "previous_month":
      return "month";
    case "latest_quarter":
    case "previous_quarter":
      return "quarter";
    case "latest_semester":
    case "previous_semester":
      return "semester";
    case "custom_period":
      return "period";
    default:
      return "period";
  }
}

/**
 * Get the period type label for a given TimeUnit selection (for Line Chart).
 */
export function getPeriodTypeLabelForTimeUnit(timeUnit: TimeUnit): string {
  switch (timeUnit) {
    case "quarter":
      return "quarter";
    case "semester":
      return "semester";
    case "month":
      return "month";
    case "week":
      return "week";
    default:
      return "period";
  }
}

/**
 * Get the rolling window size options based on the selected time period.
 * Quarter-based: 2, 3, 4 periods
 * Semester-based: 2, 3, 4 periods
 * Custom: 2, 3, 4 periods
 */
export function getRollingWindowOptions(
  timePeriod: TimePeriod,
): WindowOption[] {
  switch (timePeriod) {
    case "latest_month":
    case "previous_month":
      return [
        { value: 2, label: "2 Periods (2 months)" },
        { value: 3, label: "3 Periods (3 months)" },
        { value: 6, label: "6 Periods (6 months)" },
        { value: 12, label: "12 Periods (12 months)" },
        { value: 18, label: "18 Periods (18 months)" },
      ];
    case "latest_quarter":
    case "previous_quarter":
      return [
        { value: 2, label: "2 Periods (2 quarters / 6 months)" },
        { value: 3, label: "3 Periods (3 quarters / 9 months)" },
        { value: 4, label: "4 Periods (4 quarters / 12 months)" },
      ];
    case "latest_semester":
    case "previous_semester":
      return [
        { value: 2, label: "2 Periods (2 semesters / 12 months)" },
        { value: 3, label: "3 Periods (3 semesters / 18 months)" },
        { value: 4, label: "4 Periods (4 semesters / 24 months)" },
      ];
    case "custom_period":
      return [
        { value: 2, label: "2 Periods" },
        { value: 3, label: "3 Periods" },
        { value: 4, label: "4 Periods" },
      ];
    default:
      return [];
  }
}

/**
 * Get the rolling window size options based on the selected TimeUnit (for Line Chart).
 * Implements PRD-specified options for each time unit.
 */
export function getRollingWindowOptionsByTimeUnit(
  timeUnit: TimeUnit,
): WindowOption[] {
  switch (timeUnit) {
    case "quarter":
      return [
        { value: 2, label: "2 Periods (2 quarters / 6 months)" },
        { value: 3, label: "3 Periods (3 quarters / 9 months)" },
        { value: 4, label: "4 Periods (4 quarters / 12 months)" },
      ];
    case "semester":
      return [
        { value: 2, label: "2 Periods (2 semesters / 12 months)" },
        { value: 3, label: "3 Periods (3 semesters / 18 months)" },
      ];
    case "month":
      return [
        { value: 2, label: "2 Periods (2 months)" },
        { value: 3, label: "3 Periods (3 months)" },
        { value: 6, label: "6 Periods (6 months)" },
        { value: 12, label: "12 Periods (12 months)" },
      ];
    case "week":
      return [
        { value: 2, label: "2 Periods (2 weeks)" },
        { value: 4, label: "4 Periods (4 weeks)" },
        { value: 8, label: "8 Periods (8 weeks)" },
        { value: 12, label: "12 Periods (12 weeks)" },
      ];
    default:
      return [];
  }
}

/**
 * Default rolling window size is always 2.
 */
export function getDefaultWindowSize(): number {
  return 2;
}

/**
 * Get rolling average description for footer.
 * Format: "Rolling Average: [N] [period type]s"
 * Example: "Rolling Average: 4 quarters"
 */
export function getRollingAverageDescriptionForLineChart(
  windowSize: number,
  timeUnit: TimeUnit,
): string {
  const periodLabels: Record<TimeUnit, string> = {
    quarter: "quarter",
    semester: "semester",
    month: "month",
    week: "week",
  };
  const periodLabel = periodLabels[timeUnit];
  const plural = windowSize > 1 ? "s" : "";
  return `Rolling Average: ${windowSize} ${periodLabel}${plural}`;
}

/**
 * Get rolling average note for footer.
 * "Note: Some data points show partial rolling averages due to limited preceding data."
 */
export function getRollingAverageNoteForLineChart(windowSize: number): string {
  return "Note: Some data points show partial rolling averages due to limited preceding data.";
}

/**
 * Get helper text for the rolling average configuration.
 * "Includes the selected period plus [N-1] preceding [period type]s"
 */
export function getRollingAverageHelperText(
  windowSize: number,
  timePeriod: TimePeriod,
): string {
  const periodType = getPeriodTypeLabel(timePeriod);
  const preceding = windowSize - 1;
  const plural = preceding > 1 ? "s" : "";
  return `Includes the selected period plus ${preceding} preceding ${periodType}${plural}`;
}

/**
 * Get title suffix for rolling average.
 * Returns " (N-Period Rolling Average)"
 */
export function getRollingAverageTitleSuffix(windowSize: number): string {
  return ` (${windowSize}-Period Rolling Average)`;
}

/**
 * Compute the nominal representation of the start period for a rolling average window.
 * Goes back (windowSize - 1) periods from the selected time period.
 *
 * When endDate is provided (resolved from the dataset availability cutoff), it is used
 * as the reference date — avoiding drift from new Date() (today).
 */
function getRollingStartPeriodRepresentation(
  windowSize: number,
  timePeriod: TimePeriod,
  endDate?: string | null,
): string | null {
  const referenceDate = endDate ? parseLocalDate(endDate) : new Date();

  switch (timePeriod) {
    case "latest_month":
    case "previous_month": {
      const totalMonths =
        referenceDate.getFullYear() * 12 +
        referenceDate.getMonth() -
        (windowSize - 1);
      const startYear = Math.floor(totalMonths / 12);
      const startMonth = ((totalMonths % 12) + 12) % 12;
      return `${MONTH_NAMES[startMonth]} ${startYear}`;
    }
    case "latest_quarter":
    case "previous_quarter": {
      const refQuarter = Math.floor(referenceDate.getMonth() / 3) + 1;
      let startQuarter = refQuarter - (windowSize - 1);
      let startYear = referenceDate.getFullYear();
      while (startQuarter <= 0) {
        startQuarter += 4;
        startYear -= 1;
      }
      return `Q${startQuarter} ${startYear}`;
    }
    case "latest_semester":
    case "previous_semester": {
      const refSemester = referenceDate.getMonth() < 6 ? 1 : 2;
      let startSemester = refSemester - (windowSize - 1);
      let startYear = referenceDate.getFullYear();
      while (startSemester <= 0) {
        startSemester += 2;
        startYear -= 1;
      }
      return `Semester ${startSemester} ${startYear}`;
    }
    default:
      return null;
  }
}

/**
 * Get footer text for rolling average.
 * For period-based: "Rolling Average: [N] periods ([Start Period] - [End Period])"
 * For custom-based: "Rolling Average: [N] periods (Custom Periods)"
 */
export function getRollingAverageFooterText(
  windowSize: number,
  timePeriod: TimePeriod,
  startDate?: string | null,
  endDate?: string | null,
): string {
  const plural = windowSize > 1 ? "s" : "";

  if (timePeriod === "custom_period") {
    if (startDate && endDate) {
      const ranges = computeRollingAverageComparisonRanges(
        windowSize,
        startDate,
        endDate,
      );
      return `Rolling Average: ${windowSize} period${plural} (${ranges.current.startDate} - ${ranges.current.endDate})`;
    }
    return `Rolling Average: ${windowSize} period${plural} (Custom Periods)`;
  }

  // For period-based time periods, compute start and end period representations
  const endPeriodRepr = getTimePeriodNominalRepresentation(
    timePeriod,
    startDate,
    endDate,
  );
  const startPeriodRepr = getRollingStartPeriodRepresentation(
    windowSize,
    timePeriod,
    endDate,
  );

  if (startPeriodRepr && endPeriodRepr) {
    return `Rolling Average: ${windowSize} period${plural} (${startPeriodRepr} - ${endPeriodRepr})`;
  }

  // Fallback
  return `Rolling Average: ${windowSize} period${plural}`;
}

/**
 * Generate a missing data warning message for rolling average when the rolling window
 * has missing periods (detected via backend-provided data).
 *
 * Uses effectiveRollingPeriodCount and missingRollingPeriodLabels from the API response
 * to determine if data is incomplete and which periods are missing.
 *
 * Returns null if no warning is needed (all periods have data).
 *
 * Format: "Rolling average calculated using [M] of [N] periods due to missing data in [period list]"
 */
export function getMissingDataWarning(
  windowSize: number,
  effectiveRollingPeriodCount?: number,
  missingRollingPeriodLabels?: string[],
): string | null {
  if (!missingRollingPeriodLabels || missingRollingPeriodLabels.length === 0) {
    return null;
  }

  const periodsWithData =
    effectiveRollingPeriodCount ??
    windowSize - missingRollingPeriodLabels.length;

  return null;
}

/**
 * Format a Date object to mm/dd/yyyy string.
 */
function formatDateToDisplay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Parse a date string (YYYY-MM-DD) to a Date object in local time.
 */
function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Compute the date ranges for rolling average comparison.
 *
 * Let N = windowSize - 1 (rolling period count), span = period length in months.
 *
 * Period 1 (current rolling window):
 *   startDate = selectedStartDate - N * span
 *   endDate   = selectedEndDate
 *
 * Period 2 (previous overlapping window):
 *   startDate = selectedStartDate - (N + 1) * span
 *   endDate   = selectedEndDate - span
 *
 * Examples:
 *   Q4 (Oct01–Dec31), windowSize=3 (N=2), span=3mo:
 *     current Apr01–Dec31, previous Jan01–Sep30
 *   H2 (Jul01–Dec31), windowSize=2 (N=1), span=6mo:
 *     current Jan01 2025–Dec31 2025, previous Jul01 2024–Jun30 2025
 */
export interface RollingAverageDateRange {
  startDate: string; // mm/dd/yyyy
  endDate: string; // mm/dd/yyyy
}

export interface RollingAverageComparisonRanges {
  current: RollingAverageDateRange;
  previous: RollingAverageDateRange;
}

export function computeRollingAverageComparisonRanges(
  windowSize: number,
  selectedStartDate: string, // YYYY-MM-DD
  selectedEndDate: string, // YYYY-MM-DD
): RollingAverageComparisonRanges {
  const start = parseLocalDate(selectedStartDate);
  const end = parseLocalDate(selectedEndDate);

  // Period span in whole months (e.g. Oct01–Dec31 = 3, Jul01–Dec31 = 6)
  const spanMonths =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth() + 1);

  const N = windowSize - 1; // Number of preceding periods in the rolling average

  // Period 1 (current): startDate - N*span  →  selectedEndDate
  const currentStartDate = new Date(start);
  currentStartDate.setMonth(currentStartDate.getMonth() - N * spanMonths);
  const currentEndDate = end;

  // Period 2 (previous): startDate - (N+1)*span  →  endDate - span
  const previousStartDate = new Date(start);
  previousStartDate.setMonth(
    previousStartDate.getMonth() - (N + 1) * spanMonths,
  );
  // Set to last day of the target month: go to first day of the month after, then subtract 1 day
  const previousEndDate = new Date(end);
  previousEndDate.setMonth(previousEndDate.getMonth() - spanMonths + 1, 0);

  return {
    current: {
      startDate: formatDateToDisplay(currentStartDate),
      endDate: formatDateToDisplay(currentEndDate),
    },
    previous: {
      startDate: formatDateToDisplay(previousStartDate),
      endDate: formatDateToDisplay(previousEndDate),
    },
  };
}

/**
 * Get the rolling average comparison footer text.
 * Format: "Rolling Average Comparison: [N] periods ([Start1] - [End1]) vs. Previous overlapping [N] periods ([Start2] - [End2])"
 *
 * For period-based: uses nominal representations (Q1 2025, Semester 2 2024, etc.)
 * For custom-based: uses computed date ranges (mm/dd/yyyy)
 *
 * Returns null if dates are not available.
 */
export function getRollingAverageComparisonFooterText(
  windowSize: number,
  timePeriod: TimePeriod,
  startDate?: string | null,
  endDate?: string | null,
): string | null {
  const plural = windowSize > 1 ? "s" : "";

  if (!startDate || !endDate) {
    return null;
  }

  if (timePeriod === "custom_period") {
    // For custom periods, compute date ranges
    const ranges = computeRollingAverageComparisonRanges(
      windowSize,
      startDate,
      endDate,
    );
    return `Rolling Average Comparison: ${windowSize} period${plural} (${ranges.current.startDate} - ${ranges.current.endDate}) vs. Previous overlapping ${windowSize} period${plural} (${ranges.previous.startDate} - ${ranges.previous.endDate})`;
  }

  // For period-based time periods, use nominal representations
  const endPeriodRepr = getTimePeriodNominalRepresentation(
    timePeriod,
    startDate,
    endDate,
  );
  const startPeriodRepr = getRollingStartPeriodRepresentation(
    windowSize,
    timePeriod,
    endDate,
  );

  if (startPeriodRepr && endPeriodRepr) {
    // Compute previous window nominal representations
    // Previous window end = one period before the current window start
    // Previous window covers windowSize periods ending just before the current window starts
    const prevEndPeriodRepr = getPreviousWindowEndPeriodRepresentation(
      timePeriod,
      endDate,
    );
    const prevStartPeriodRepr = getPreviousWindowStartPeriodRepresentation(
      windowSize,
      timePeriod,
      endDate,
    );

    if (prevStartPeriodRepr && prevEndPeriodRepr) {
      return `Rolling Average Comparison: ${windowSize} period${plural} (${startPeriodRepr} - ${endPeriodRepr}) vs. Previous overlapping ${windowSize} period${plural} (${prevStartPeriodRepr} - ${prevEndPeriodRepr})`;
    }
  }

  // Fallback to date-based computation
  const ranges = computeRollingAverageComparisonRanges(
    windowSize,
    startDate,
    endDate,
  );
  return `Rolling Average Comparison: ${windowSize} period${plural} (${ranges.current.startDate} - ${ranges.current.endDate}) vs. Previous overlapping ${windowSize} period${plural} (${ranges.previous.startDate} - ${ranges.previous.endDate})`;
}

/**
 * Get the nominal representation for the end period of the previous rolling window.
 * This is one period before the current window's start period (which is one period before the selected period).
 * For example, if selected is Q4 2025 with windowSize=3, current window is Q2-Q4 2025,
 * and previous window ends at Q1 2025.
 */
function getPreviousWindowEndPeriodRepresentation(
  timePeriod: TimePeriod,
  endDate?: string | null,
): string | null {
  const referenceDate = endDate ? parseLocalDate(endDate) : new Date();

  switch (timePeriod) {
    case "latest_month":
    case "previous_month": {
      const totalMonths =
        referenceDate.getFullYear() * 12 + referenceDate.getMonth() - 1;
      const prevEndYear = Math.floor(totalMonths / 12);
      const prevEndMonth = ((totalMonths % 12) + 12) % 12;
      return `${MONTH_NAMES[prevEndMonth]} ${prevEndYear}`;
    }
    case "latest_quarter":
    case "previous_quarter": {
      const refQuarter = Math.floor(referenceDate.getMonth() / 3) + 1;
      let prevEndQuarter = refQuarter - 1;
      let prevEndYear = referenceDate.getFullYear();
      if (prevEndQuarter <= 0) {
        prevEndQuarter += 4;
        prevEndYear -= 1;
      }
      return `Q${prevEndQuarter} ${prevEndYear}`;
    }
    case "latest_semester":
    case "previous_semester": {
      const refSemester = referenceDate.getMonth() < 6 ? 1 : 2;
      let prevEndSemester = refSemester - 1;
      let prevEndYear = referenceDate.getFullYear();
      if (prevEndSemester <= 0) {
        prevEndSemester += 2;
        prevEndYear -= 1;
      }
      return `Semester ${prevEndSemester} ${prevEndYear}`;
    }
    default:
      return null;
  }
}

/**
 * Get the nominal representation for the start period of the previous rolling window.
 * Goes back windowSize periods from the previous window's end period.
 *
 * When endDate is provided (resolved from the dataset availability cutoff), it is used
 * as the reference date — avoiding drift from new Date() (today).
 */
function getPreviousWindowStartPeriodRepresentation(
  windowSize: number,
  timePeriod: TimePeriod,
  endDate?: string | null,
): string | null {
  const referenceDate = endDate ? parseLocalDate(endDate) : new Date();

  switch (timePeriod) {
    case "latest_month":
    case "previous_month": {
      const prevEndTotalMonths =
        referenceDate.getFullYear() * 12 + referenceDate.getMonth() - 1;
      const startTotalMonths = prevEndTotalMonths - (windowSize - 1);
      const startYear = Math.floor(startTotalMonths / 12);
      const startMonth = ((startTotalMonths % 12) + 12) % 12;
      return `${MONTH_NAMES[startMonth]} ${startYear}`;
    }
    case "latest_quarter":
    case "previous_quarter": {
      const refQuarter = Math.floor(referenceDate.getMonth() / 3) + 1;
      let prevEndQuarter = refQuarter - 1;
      let prevEndYear = referenceDate.getFullYear();
      if (prevEndQuarter <= 0) {
        prevEndQuarter += 4;
        prevEndYear -= 1;
      }
      let startQuarter = prevEndQuarter - (windowSize - 1);
      let startYear = prevEndYear;
      while (startQuarter <= 0) {
        startQuarter += 4;
        startYear -= 1;
      }
      return `Q${startQuarter} ${startYear}`;
    }
    case "latest_semester":
    case "previous_semester": {
      const refSemester = referenceDate.getMonth() < 6 ? 1 : 2;
      let prevEndSemester = refSemester - 1;
      let prevEndYear = referenceDate.getFullYear();
      if (prevEndSemester <= 0) {
        prevEndSemester += 2;
        prevEndYear -= 1;
      }
      let startSemester = prevEndSemester - (windowSize - 1);
      let startYear = prevEndYear;
      while (startSemester <= 0) {
        startSemester += 2;
        startYear -= 1;
      }
      return `Semester ${startSemester} ${startYear}`;
    }
    default:
      return null;
  }
}

/**
 * Generate a missing data warning message for line chart rolling average
 * when the rolling window extends before the dataset's responsesMinDate.
 *
 * Line charts use TimeUnit (quarter/semester/month/week) with explicit start/end dates.
 *
 * Returns null if no warning is needed.
 */
export function getMissingDataWarningForLineChart(
  windowSize: number,
  timeUnit: TimeUnit,
  startDate: string | null,
  responsesMinDate?: string | null,
): string | null {
  if (!responsesMinDate || !startDate) return null;

  const start = parseLocalDate(startDate);
  const minDate = parseLocalDate(responsesMinDate);

  // Calculate the rolling window start date by going back (windowSize - 1) time units
  let windowStartDate: Date;
  switch (timeUnit) {
    case "quarter": {
      windowStartDate = new Date(start);
      windowStartDate.setMonth(
        windowStartDate.getMonth() - (windowSize - 1) * 3,
      );
      break;
    }
    case "semester": {
      windowStartDate = new Date(start);
      windowStartDate.setMonth(
        windowStartDate.getMonth() - (windowSize - 1) * 6,
      );
      break;
    }
    case "month": {
      windowStartDate = new Date(start);
      windowStartDate.setMonth(windowStartDate.getMonth() - (windowSize - 1));
      break;
    }
    case "week": {
      windowStartDate = new Date(start);
      windowStartDate.setDate(windowStartDate.getDate() - (windowSize - 1) * 7);
      break;
    }
    default:
      return null;
  }

  // If the rolling window start is on or after responsesMinDate, all periods have data
  if (windowStartDate >= minDate) return null;

  // Count missing periods
  let missingCount = 0;
  const periodLabels: string[] = [];

  for (let i = windowSize - 1; i >= 0; i--) {
    const periodStart = new Date(start);
    switch (timeUnit) {
      case "quarter":
        periodStart.setMonth(periodStart.getMonth() - i * 3);
        break;
      case "semester":
        periodStart.setMonth(periodStart.getMonth() - i * 6);
        break;
      case "month":
        periodStart.setMonth(periodStart.getMonth() - i);
        break;
      case "week":
        periodStart.setDate(periodStart.getDate() - i * 7);
        break;
    }

    if (periodStart < minDate) {
      missingCount++;

      // Generate a label for the missing period
      switch (timeUnit) {
        case "quarter": {
          const q = Math.floor(periodStart.getMonth() / 3) + 1;
          periodLabels.push(`Q${q} ${periodStart.getFullYear()}`);
          break;
        }
        case "semester": {
          const s = periodStart.getMonth() < 6 ? 1 : 2;
          periodLabels.push(`Semester ${s} ${periodStart.getFullYear()}`);
          break;
        }
        case "month": {
          const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          periodLabels.push(
            `${monthNames[periodStart.getMonth()]} ${periodStart.getFullYear()}`,
          );
          break;
        }
        case "week": {
          const m = String(periodStart.getMonth() + 1).padStart(2, "0");
          const d = String(periodStart.getDate()).padStart(2, "0");
          periodLabels.push(`Week of ${m}/${d}/${periodStart.getFullYear()}`);
          break;
        }
      }
    }
  }

  if (missingCount === 0) return null;

  return null;
}

/**
 * Count the number of complete time periods between startDate and endDate (inclusive).
 * Used to determine totalAvailablePeriods when breakout is active.
 */
export function countPeriodsInRange(
  startDate: string | null,
  endDate: string | null,
  timeUnit: TimeUnit,
): number {
  if (!startDate || !endDate) return 0;
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  switch (timeUnit) {
    case "month":
      return (
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth()) +
        1
      );
    case "quarter": {
      const startQ = Math.floor(start.getMonth() / 3);
      const endQ = Math.floor(end.getMonth() / 3);
      return (
        (end.getFullYear() - start.getFullYear()) * 4 + (endQ - startQ) + 1
      );
    }
    case "semester": {
      const startS = Math.floor(start.getMonth() / 6);
      const endS = Math.floor(end.getMonth() / 6);
      return (
        (end.getFullYear() - start.getFullYear()) * 2 + (endS - startS) + 1
      );
    }
    case "week":
      return (
        Math.floor(
          (end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000),
        ) + 1
      );
    default:
      return 0;
  }
}
